import { useEffect, useState } from 'preact/hooks';
import { api } from '../services/api.js';

interface NearestLocationsResponse {
  items: { _id: string }[];
}

/**
 * 125 D6: short client-side position timeout, distinct from useFetch's own
 * READ_TIMEOUT_MS — this governs the device's position fix, not the network read
 * that follows it.
 */
const POSITION_TIMEOUT_MS = 5000;

/**
 * 125: isolates the browser's geolocation → nearest-location-lookup sequence behind
 * a hook, so every outcome (denied, unsupported, timeout, lookup failure, empty
 * result) can be simulated directly in a test rather than through a rendered page.
 *
 * Every failure path resolves `null` and never throws — a caller applies the result
 * only as an optional pre-selection, never as something to react to with an error.
 *
 * 127: `enabled` (default `true`, preserving `HomePage.tsx`'s original call byte-for-byte)
 * gates the geolocation request itself, not just the returned value — a caller in a mode
 * that has no use for the result (e.g. a location-scoped search) must not trigger a real
 * browser location-permission prompt for nothing.
 */
export function useNearestLocationId(enabled = true): string | null {
  const [nearestId, setNearestId] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !navigator.geolocation) return;
    let cancelled = false;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await api.get<NearestLocationsResponse>(
            `/locations/nearest?lat=${latitude}&lng=${longitude}`,
          );
          if (!cancelled) setNearestId(response.items[0]?._id ?? null);
        } catch {
          if (!cancelled) setNearestId(null);
        }
      },
      () => {
        if (!cancelled) setNearestId(null);
      },
      { timeout: POSITION_TIMEOUT_MS },
    );

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return nearestId;
}
